import {
  CompanyBreakdownDisplayMode,
  InvestmentIdentifierDisplayMode,
  ShowSubscriptionPagePerformanceChartMode,
  SyndicatesArrangement,
  TaxReliefDisplayMode,
  TileType,
} from 'further-types/firm';
import { sortBy } from 'lodash';
import isMongoId from '../../helpers/is-mongo-id';
import { z } from 'zod';

const refineMultipleEmails = (value: string | undefined) => {
  if (!value) return true;
  const trimmedValue = value.split(';').map((email) => email.trim());
  const isValid = trimmedValue.every((email) => {
    if (email) {
      return z.string().email().safeParse(email).success;
    }
    return true;
  });
  return isValid;
};

const validateWebhookUrl = (value: string | undefined) => {
  if (!value) return true;

  try {
    const url = new URL(value);
    const fullUrl = url.toString().toLowerCase();

    if (url.protocol !== 'https:') {
      return false;
    }

    // Block IPv4 addresses
    const ipv4Regex = /(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})/;
    if (ipv4Regex.test(fullUrl)) {
      return false;
    }

    // Block IPv6 addresses
    const ipv6Regex = /\[?([0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}\]?/;
    if (ipv6Regex.test(fullUrl)) {
      return false;
    }

    // Block localhost and variants
    const localhostRegex = /^localhost(\..+)?$/i;
    if (localhostRegex.test(url.hostname)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

// Create base schema
const EisFirmBaseSchema = z.object({
  firmName: z
    .string({ required_error: 'Please enter a firm name.' })
    .min(1, 'Please enter a firm name.'),
  keyContactName: z
    .string({ required_error: 'Please enter a key contact name.' })
    .min(1, 'Please enter a key contact name.'),
  keyContactEmail: z
    .string({ required_error: 'Please enter a valid email address.' })
    .email({ message: 'Please enter a valid email address.' })
    .min(1, 'Please enter a valid email address.'),
  secondaryContactName: z.string().optional(),
  secondaryContactEmail: z
    .string()
    .optional()
    .refine((value) => !value || z.string().email().safeParse(value).success, {
      message: 'Please enter a valid email address.',
    }),
  summaryAttachments: z.array(
    z.object({
      fileName: z.string(),
      filePath: z.string(),
      preview: z.string().optional(),
    }),
  ),
  systemEmailRecipient: z.string().optional().refine(refineMultipleEmails),
  excelFilesPassword: z
    .string()
    .optional()
    .refine((value) => !value || value.length >= 8, {
      message: 'Please enter a password with at least 8 characters.',
    }),
  excelFilesPasswordExists: z.boolean().optional(),
  newInvestmentWebhookUrl: z
    .string()
    .optional()
    .refine((value) => !value || validateWebhookUrl(value), {
      message: 'Please enter a valid HTTPS URL.',
    }),
});

// Password validation refinement
const passwordValidation = (data: z.infer<typeof EisFirmBaseSchema>) => {
  // If excelFilesPasswordExists is true, password must be provided
  if (data.excelFilesPasswordExists && !data.excelFilesPassword) {
    return false;
  }
  return true;
};

const passwordValidationError = {
  message:
    'Password is required when protect spreadsheets sent in emails is selected',
  path: ['excelFilesPassword'],
};

// Apply refinement for create schema
export const CreateEisFirmInput = EisFirmBaseSchema.refine(
  passwordValidation,
  passwordValidationError,
);

// Apply extend and refinement for update schema
export const UpdateEisFirmGeneralInformationInput = EisFirmBaseSchema.extend({
  firmId: z.string().refine(isMongoId()),
}).refine(passwordValidation, passwordValidationError);

export const UpdateEisFirmWhiteLabelSettingsInput = z.object({
  firmId: z.string().refine(isMongoId()),
  whiteLabelConfig: z.object({
    bgColor: z.string().optional(),
    bgColorContrastText: z.string().optional(),
    lightBackgroundIconColor: z.string().optional(),
    bgColorAlternate: z.string().optional(),
    bgColorAlternateContrastText: z.string().optional(),
    primaryColor: z.string().optional(),
    primaryContrastText: z.string().optional(),
    primaryHoverColor: z.string().optional(),
    secondaryColor: z.string().optional(),
    clientServiceEmail: z
      .string({ required_error: 'Please enter a valid email address.' })
      .email({ message: 'Please enter a valid email address.' })
      .optional(),
    emailFooterText: z.string().optional(),
    portalFooterText: z.string().optional(),
    whiteBgLogo: z
      .object({
        key: z.string(),
        url: z.string().optional(),
      })
      .optional(),
    colorBgLogo: z
      .object({
        key: z.string(),
        url: z.string().optional(),
      })
      .optional(),
  }),
  contactDetails: z.object({
    keyChecks: z.array(z.string().nullish()),
    phoneHours: z.string().optional(),
    email: z
      .string()
      .email({ message: 'Please enter a valid email address.' })
      .optional(),
    phoneNo: z.string().optional(),
  }),
  domain: z.string({ required_error: 'Please enter subdomain.' }),
  firmLogo: z.string().optional(),
  investmentFirmLogo: z.string().optional(),
});

export const UpdateEisPortalAndKycSettingsInput = z.object({
  firmId: z.string().refine(isMongoId()),
  kycKybReviewRecipient: z.string().optional().refine(refineMultipleEmails),
  amlReviewRecipient: z.string().optional().refine(refineMultipleEmails),
  whiteLabelConfig: z.object({
    allowPortalCashWithdrawals: z.boolean(),
    showMultipleOnInvestment: z.boolean(),
    showMultipleOnInvestedCapital: z.boolean(),
    showInvestmentLink: z.boolean(),
    enableKyb: z.boolean(),
    proceedWithConsiderStatus: z.boolean(),
    manuallyRequestKybDocumentation: z.boolean(),
    kybServiceLink: z.string().url().optional().or(z.literal('')),
    customDocumentRequestText: z.string().optional(),
    configurableTile1: z.nativeEnum(TileType),
    configurableTile2: z.nativeEnum(TileType),
    cashBalanceUsable: z.boolean(),
    hideCashBalanceBefore: z.date().nullable(),
    taxReliefDisplayMode: z.nativeEnum(TaxReliefDisplayMode),
    companyBreakdownDisplayMode: z.nativeEnum(CompanyBreakdownDisplayMode),
    syndicatePortalDisplayName: z
      .string({
        required_error: 'Please enter a display name for syndicates.',
      })
      .min(1, 'Please enter a display name for syndicates.'),
    investmentIdentifierDisplayMode: z.nativeEnum(
      InvestmentIdentifierDisplayMode,
    ),
    showFeeBreakdownToInvestors: z.boolean(),
    showAccruedFeesInFeeBreakdown: z.boolean(),
    showSubscriptionPagePerformanceChart: z.nativeEnum(
      ShowSubscriptionPagePerformanceChartMode,
    ),
    syndicatesArrangement: z.nativeEnum(SyndicatesArrangement),
    permittedClassifications: z.object({
      everyday: z.boolean(),
      sophisticated: z.boolean(),
      highNetWorth: z.boolean(),
      professional: z.boolean(),
    }),
    showAppropriatenessTest: z.boolean(),
  }),
});

export const UpdateEisFirmBankAccountAndFeesSettingsInput = z.object({
  firmId: z.string().refine(isMongoId()),
  bankAccountForFeesExport: z.object({
    accountName: z
      .string({ required_error: 'Please enter a bank account name.' })
      .min(1, 'Please enter a bank account name.'),
    accountNumber: z
      .string({ required_error: 'Please enter a bank account number.' })
      .min(1, 'Please enter a bank account number.')
      .regex(/^[0-9]{8}$/g, {
        message: 'Please enter an 8 digit account number.',
      }),
    accountSortCode: z
      .string({ required_error: 'Please enter a bank account sort code.' })
      .min(1, 'Please enter a bank account sort code.')
      .regex(/^[0-9]{2}-[0-9]{2}-[0-9]{2}$/, {
        message: 'Please enter an 6 digit account sort code.',
      }),
    reference: z
      .string({ required_error: 'Please enter a bank transfer reference.' })
      .min(1, 'Please enter a bank transfer reference.'),
  }),
  withdrawalFeeText: z.string().optional(),
  withdrawalFees: z
    .array(
      z.object({
        vat: z.number(),
        amount: z.number().positive(),
        rangeEnd: z.number().optional().nullable(),
        rangeStart: z.number().optional().nullable(),
      }),
    )
    .refine(
      (fees) => {
        if (!fees.length) return true;

        return !fees.some(
          (fee) => fee.rangeEnd && !fee.rangeStart && fee.rangeStart !== 0,
        );
      },
      {
        message:
          'A `From` value must be entered if a `To` value is supplied. Please adjust the ranges accordingly.',
      },
    )
    .refine(
      (fees) => {
        if (!fees.length) return true;

        return !fees.some(
          (fee) => fee.rangeEnd && (fee.rangeStart ?? 0) >= fee.rangeEnd,
        );
      },
      {
        message:
          'The start of each range must be less than the end of the range. Please adjust the ranges accordingly.',
      },
    )
    .refine(
      (fees) => {
        if (!fees.length) return true;

        // Sort fees by rangeStart to simplify overlap checking
        const sortedFees = sortBy(fees, 'rangeStart', 'asc');

        return !sortedFees.find(
          (fee, idx) =>
            sortedFees[idx - 1] &&
            fee.rangeStart &&
            fee.rangeStart <= (sortedFees[idx - 1].rangeEnd ?? Infinity), // If the previous fee has no rangeEnd, no new higher ranges can apply
        );
      },
      {
        message:
          'There is an overlap in the withdrawal fee ranges. Please adjust the ranges accordingly.',
      },
    )
    .refine(
      (fees) => {
        if (!fees.length) return true;

        // An open range must be the only fee
        return !(
          fees.length > 1 &&
          fees.some((fee) => !fee.rangeStart && !fee.rangeEnd)
        );
      },
      {
        message:
          'A fee with no range must be the only fee. Please adjust the ranges accordingly',
      },
    ),
});

export const EisFirmIdInput = z.object({
  firmId: z.string().refine(isMongoId()),
});
